// src/components/DocumentForgery/DocumentCard.jsx
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText } from 'lucide-react';
import { useState } from 'react';
import PdfThumbnail from './../PdfThumbnail';

const getRiskColor = (score) => {
  if (score === null || score === undefined) return null;
  if (score >= 70) return { color: '#ef4444', bg: '#fef2f2', border: '#fecaca' };
  if (score >= 40) return { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' };
  return          { color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0' };
};

const getRiskLabel = (score) => {
  if (score === null || score === undefined) return null;
  if (score >= 90) return 'CRITICAL';
  if (score >= 70) return 'HIGH';
  if (score >= 40) return 'MED';
  return 'LOW';
};

const STATUS_CFG = {
  marked_for_review: { label: '🔖 Review',  color: '#15803d', bg: '#dcfce7', border: '#bbf7d0' },
  accepted:          { label: '✓ Accepted', color: '#0284c7', bg: '#e0f2fe', border: '#bae6fd' },
  rejected:          { label: '✗ Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fecaca' },
};

const DocumentCard = ({ data }) => {
  const navigate   = useNavigate();
  const [imgError, setImgError] = useState(false);
  const [hovered,  setHovered]  = useState(false);

  const documentUrl = data?.document_url || data?.doc_url || '';
  const rawFormat   = data?.doc_format || data?.format || '';
  const format      = rawFormat?.toLowerCase()?.split('/')?.pop() || '';
  const isImage     = ['png', 'jpg', 'jpeg', 'webp', 'tiff'].includes(format)
                      || rawFormat?.startsWith?.('image/');
  const isPdf       = format === 'pdf' || rawFormat === 'application/pdf';
  const docName     = data?.document_name || data?.name || 'Unknown';

  const createdAt = data?.created_at
    ? new Date(data.created_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : '—';

  const score     = data?.overall_risk_score ?? data?.risk_score ?? null;
  const riskCfg   = getRiskColor(score);
  const riskLabel = getRiskLabel(score);
  const docId     = data?.document_id || data?.id;
  const status    = data?.status || 'pending';
  const statusCfg = STATUS_CFG[status] || null;

  const isHighRisk = score !== null && score >= 70;
  const isReview   = status === 'marked_for_review';

  return (
    <div
      className="file-card"
      onClick={() => navigate(`/dashboard/analysis/${docId}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor:        'pointer',
        display:       'flex',
        flexDirection: 'column',
        width:         '100%',          // fills the grid column
        borderRadius:  12,
        overflow:      'hidden',
        background:    '#fff',
        border:        `1px solid ${hovered ? '#d1d5db' : '#e5e7eb'}`,
        // High-risk: bottom accent bar only
        borderBottom:  isHighRisk
          ? '3px solid #ef4444'
          : isReview
          ? '3px solid #16a34a'
          : `1px solid ${hovered ? '#d1d5db' : '#e5e7eb'}`,
        boxShadow: hovered
          ? '0 8px 24px rgba(0,0,0,0.10)'
          : '0 1px 3px rgba(0,0,0,0.06)',
        transform:   hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition:  'box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease',
      }}
    >
      {/* ── Thumbnail — 16:10 aspect ratio, always consistent ── */}
      <div
        className="file-card__thumb"
        style={{
          // paddingTop trick: 16:10 = 62.5% height relative to width
          position:   'relative',
          width:      '100%',
          paddingTop: '62.5%',
          background: '#f3f4f6',
          overflow:   'hidden',
          flexShrink: 0,
        }}
      >
        {/* All content inside is absolutely positioned to fill the ratio box */}
        <div style={{ position: 'absolute', inset: 0 }}>
          {isPdf ? (
            <div style={{ width: '100%', height: '100%' }}>
              <PdfThumbnail file={documentUrl} />
            </div>
          ) : isImage && documentUrl && !imgError ? (
            <img
              src={documentUrl}
              alt={docName}
              onError={() => setImgError(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 8,
              background: 'linear-gradient(145deg, #f0f7ff 0%, #e8f4fd 100%)',
            }}>
              <FileText size={32} color="#44ace4" strokeWidth={1.5} />
              <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {format || 'Document'}
              </span>
            </div>
          )}
        </div>

        {/* ── Overlay badges ── */}

        {/* Format pill — bottom left */}
        <span style={{
          position: 'absolute', bottom: 8, left: 8,
          background: 'rgba(15,23,42,0.72)',
          backdropFilter: 'blur(4px)',
          color: '#fff', borderRadius: 6,
          fontSize: 9, fontWeight: 800,
          padding: '3px 7px', textTransform: 'uppercase',
          letterSpacing: '0.4px', lineHeight: 1.4,
        }}>
          {format || 'file'}
        </span>

        {/* Risk badge — top right */}
        {riskCfg && score !== null && (
          <span style={{
            position:   'absolute', top: 8, right: 8,
            background: riskCfg.bg,
            color:      riskCfg.color,
            border:     `1px solid ${riskCfg.border}`,
            borderRadius: 20,
            fontSize:   10, fontWeight: 800,
            padding:    '3px 8px', lineHeight: 1.4,
            display:    'flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 11, fontWeight: 900 }}>{Math.round(score)}</span>
            <span style={{ fontSize: 8, opacity: 0.8 }}>{riskLabel}</span>
          </span>
        )}

        {/* Date mismatch flag — top left */}
        {data?.date_mismatch_flag && (
          <span style={{
            position: 'absolute', top: 8, left: 8,
            background: '#fef3c7', color: '#d97706',
            borderRadius: 4, padding: '3px 6px',
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <AlertTriangle size={9} />
            <span style={{ fontSize: 9, fontWeight: 700 }}>Date</span>
          </span>
        )}
      </div>

      {/* ── Info strip ── */}
      <div
        className="file-card__info"
        style={{
          padding:       '10px 12px 12px',
          display:       'flex',
          flexDirection: 'column',
          gap:           5,
          flexGrow:      1,   // only grows if there's extra space — keeps it compact
        }}
      >
        {/* Document name — max 2 lines */}
        <span
          className="file-card__name"
          title={docName}
          style={{
            fontSize:         13,
            fontWeight:       600,
            color:            '#111827',
            lineHeight:       1.45,
            display:          '-webkit-box',
            WebkitLineClamp:  2,
            WebkitBoxOrient:  'vertical',
            overflow:         'hidden',
          }}
        >
          {docName}
        </span>

        {/* Date + status on the same row */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          gap:            6,
          marginTop:      2,
        }}>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{createdAt}</span>

          {statusCfg && (
            <span style={{
              fontSize:     9,
              fontWeight:   700,
              background:   statusCfg.bg,
              color:        statusCfg.color,
              border:       `1px solid ${statusCfg.border}`,
              borderRadius: 20,
              padding:      '2px 7px',
              lineHeight:   1.5,
              whiteSpace:   'nowrap',
            }}>
              {statusCfg.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;